import type { ProviderImage } from "@louise/providers/providers.types";

export type ClinicDisplayImage = {
  url: string;
  type?: ProviderImage["type"];
};

const FACILITY_IMAGE_TYPES = new Set<NonNullable<ProviderImage["type"]>>([
  "exterior",
  "interior",
  "lab",
]);

const FACILITY_OR_LOGO_IMAGE_TYPES = new Set<
  NonNullable<ProviderImage["type"]>
>([...FACILITY_IMAGE_TYPES, "logo"]);

const BLOCKED_IMAGE_PARTS = [
  "donor-portal",
  "engagedmd",
  "patient-portal",
  "portal-eivf",
  "unsplash",
  "pexels",
  "shutterstock",
  "istock",
  "gettyimages",
  "adobestock",
  "depositphotos",
  "pixabay",
  "dreamstime",
  "stock-photo",
  "stockphoto",
  "newspaper",
  "headline",
  "magazine",
  "editorial",
  "press-release",
  "facebook",
  "social",
  "reseaux-sociaux",
  "flags/",
  ".gif",
  "illus",
  "illustr",
  "piwikpro",
  "analytics",
  "tracking",
  "parcours-de-soin",
  "quadri",
  "rvb",
  "horizontal",
  "elodie.",
  "pma.png",
  "phone_0.",
  "camp26",
  "header_mask",
  "plan_",
  "fertilization",
  "tableau",
  "praticien",
  "cancero",
  "urgences-sein",
  "nourrisson",
  "actu-2026",
  "cegidd",
  "couple",
  "homosex",
  "high-tech",
  "miniature",
  "top-activities",
  "top-praticiens",
  "soignantes",
  "pop-up",
  "mailblanc",
  "parlons-sante",
  "bandeau-rubrique",
  "banniere-site",
  "banniere",
  "bandeau",
  "en-1-clic",
  "venir-hopital",
  "laboratoire-aix",
  "images/accueil/hopital.png",
  "le-nouvel-hopital-de-nantes",
  "image-ru-site-internet",
  "pma-28342",
  "8981374-1920",
  "livret",
  "presta-",
  "analyse",
  "fiv.jpg",
  "eclipse",
  "travaux-tram",
  "vignette",
  "clement",
  "defi-clement",
  "design-sans-titre",
  "valeurs",
  "trait-bleu",
  "sobriete-numerique",
  "charte",
  "feuilleamp",
  "static_map",
  "map-pin",
  "consent",
  "cookie",
  "locationdata",
  "video",
  "youtube",
  "vimeo",
  "career",
  "placeholder",
  "business-insider",
  "sartgraph",
  "chart",
  "badge",
  "seal",
  ".svg",
];

function isAllowedImage(image: ClinicDisplayImage, allowedTypes: Set<string>) {
  if (!image.type || !allowedTypes.has(image.type)) return false;
  if (!image.url || !/^https?:\/\//i.test(image.url)) return false;

  const url = image.url.toLowerCase();
  return (
    !BLOCKED_IMAGE_PARTS.some((part) => url.includes(part)) &&
    !/(?:[?&](?:rec|idsite|bots)=)/i.test(url) &&
    !/(?:^|[/_-])fb(?:[/_.-]|$)/i.test(url)
  );
}

export function isClinicFacilityImage(image: ClinicDisplayImage) {
  return isAllowedImage(image, FACILITY_IMAGE_TYPES);
}

export function isClinicFacilityOrLogoImage(image: ClinicDisplayImage) {
  return isAllowedImage(image, FACILITY_OR_LOGO_IMAGE_TYPES);
}

export function getClinicFacilityImages<TImage extends ClinicDisplayImage>(
  images: TImage[] | undefined,
) {
  return (images ?? []).filter(isClinicFacilityImage);
}

export function getClinicFacilityOrLogoImages<
  TImage extends ClinicDisplayImage,
>(images: TImage[] | undefined) {
  return (images ?? []).filter(isClinicFacilityOrLogoImage);
}

/**
 * React Native Web fetches remote image sources as opaque resources. Clinic
 * websites and Google Business photo URLs can be rejected by the browser's
 * ORB protection even though the sources are valid images, so display those
 * same source images via the image proxy while retaining the direct URLs in
 * the data records.
 */
export function getClinicDisplayImageUrl(image: ClinicDisplayImage) {
  if (!image.url || !/^https?:\/\//i.test(image.url)) return null;
  return `https://images.weserv.nl/?url=${encodeURIComponent(image.url)}`;
}
